import type { RefObject, ComponentProps } from 'react'
import { useRef, forwardRef, useImperativeHandle } from 'react'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import type DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'

import type { CruiseLocationDesktopInputRef } from './CruiseLocationDesktopInput'
import CruiseLocationDesktopInput from './CruiseLocationDesktopInput'
import CruiseLocationMobileInput from './CruiseLocationMobileInput'
import { CruiseSearchInputProvider } from '../CruiseSearchInputContext'

export interface CruiseLocationFilterRef {
  focus: () => void
}

interface Props {
  onChange: (values: App.CruiseGlobalFilters) => void
  isChipMode?: boolean
  /** Use when this component is inside a drawer modal */
  drawerMode?: boolean
  hideImages?: boolean
  dropdownAnchorRef?: RefObject<HTMLElement | null>
  dropdownSize?: ComponentProps<typeof DropdownSheet>['size']
  hideTrendOptions?: boolean
  initialFilters?: App.CruiseInitialFilters
}

const CruiseLocationFilterInner = forwardRef<CruiseLocationFilterRef, Props>(({
  isChipMode,
  drawerMode,
  onChange,
  dropdownAnchorRef,
  dropdownSize,
  hideImages,
  initialFilters,
  hideTrendOptions,
}, ref) => {
  const inputRef = useRef<HTMLDivElement>(null)
  const desktopInputRef = useRef<CruiseLocationDesktopInputRef>(null)

  useImperativeHandle(ref, () => ({
    focus: () => {
      desktopInputRef.current?.focus()
    },
  }), [])

  return <div ref={inputRef}>
    <CSSBreakpoint min="tablet">
      {drawerMode && <CruiseLocationMobileInput
        isChipMode={isChipMode}
        onChange={onChange}
        hideImages={hideImages}
        hideTrendOptions={hideTrendOptions}
        initialFilters={initialFilters}
      />}

      {!drawerMode && <CruiseLocationDesktopInput
        ref={desktopInputRef}
        dropdownAnchorRef={dropdownAnchorRef || inputRef}
        onChange={onChange}
        isChipMode={isChipMode}
        dropdownSize={dropdownSize}
        hideImages={hideImages}
        initialFilters={initialFilters}
        hideTrendOptions={hideTrendOptions}
      />}
    </CSSBreakpoint>
    <CSSBreakpoint max="mobile">
      <CruiseLocationMobileInput
        isChipMode={isChipMode}
        onChange={onChange}
        hideImages={hideImages}
        hideTrendOptions={hideTrendOptions}
        initialFilters={initialFilters}
      />
    </CSSBreakpoint>
  </div>
})

CruiseLocationFilterInner.displayName = 'CruiseLocationFilterInner'

const CruiseLocationFilter = forwardRef<CruiseLocationFilterRef, Props>((props, ref) => (
  <CruiseSearchInputProvider>
    <CruiseLocationFilterInner {...props} ref={ref} />
  </CruiseSearchInputProvider>
))

CruiseLocationFilter.displayName = 'CruiseLocationFilter'

export default CruiseLocationFilter
